import { useState, useEffect } from 'react'
import axios from 'axios'
import { Button } from '../ui/button'
import { Input } from '../ui/input'
import { Label } from '../ui/label'
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../ui/dialog'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../ui/select'
import { ScrollArea } from '../ui/scroll-area'
import { toast } from 'sonner'
import { useLanguage } from '../../context/LanguageContext'
import { IDEA_ICON_OPTIONS, getIconLabel } from '../../lib/iconOptions'
import { 
  Plus, Circle, Sparkles, Rocket, Building2, User, Lightbulb,
  Briefcase, Brain, Laptop, Star, Heart, MoreHorizontal,
  // New icons
  FolderKanban, Package, Smartphone, Wand2, Search, FileText, Megaphone,
  BadgeCheck, Palette, Target, Cog, Bot, FlaskConical, Network, Wrench,
  GraduationCap, BookOpen, PenTool, Eye, Zap, Trophy, Compass, Map,
  GitBranch, Blocks
} from 'lucide-react'

const API = `${process.env.REACT_APP_BACKEND_URL}/api`

// Extended icon map with all idea bucket icons
const iconMap = {
  // Original icons
  Sparkles, Rocket, Building2, User, Lightbulb, Briefcase, 
  Brain, Laptop, Star, Heart, MoreHorizontal, Circle,
  // New icons
  FolderKanban, Package, Smartphone, Wand2, Search, FileText, Megaphone,
  BadgeCheck, Palette, Target, Cog, Bot, FlaskConical, Network, Wrench,
  GraduationCap, BookOpen, PenTool, Eye, Zap, Trophy, Compass, Map,
  GitBranch, Blocks
}

/**
 * Reusable modal for creating/editing idea buckets
 * Can be used from SettingsPage and CrazyIdeasPage
 */
export function IdeaBucketModal({ 
  open, 
  onOpenChange, 
  editingBucket = null,
  onSuccess
}) {
  const { locale, t } = useLanguage()
  const [saving, setSaving] = useState(false)
  const [form, setForm] = useState({
    name: '',
    icon: 'Circle'
  })

  // Reset form when modal opens/closes or editing changes
  useEffect(() => {
    if (open) {
      if (editingBucket) {
        setForm({
          name: editingBucket.name,
          icon: editingBucket.icon || 'Circle'
        })
      } else {
        setForm({
          name: '',
          icon: 'Circle'
        })
      }
    }
  }, [open, editingBucket])

  const handleSave = async () => {
    if (!form.name.trim()) {
      toast.error(t('settings.nameRequired'))
      return
    }

    if (saving) return
    setSaving(true)

    try {
      if (editingBucket) {
        await axios.put(`${API}/idea-buckets/${editingBucket.id}`, form)
        toast.success(t('ideas.bucketUpdated'))
      } else {
        await axios.post(`${API}/idea-buckets`, form)
        toast.success(t('ideas.bucketCreated'))
      }
      onOpenChange(false)
      onSuccess?.()
    } catch (error) {
      const message = error.response?.data?.detail || t('finance.errorSaving')
      toast.error(message)
    } finally {
      setSaving(false)
    }
  }

  const SelectedIcon = iconMap[form.icon] || Circle

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>
            {editingBucket 
              ? t('categoryModals.editIdeaBucket')
              : t('categoryModals.newIdeaBucket')}
          </DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="space-y-2">
            <Label>{t('common.name')} *</Label>
            <Input
              value={form.name}
              onChange={(e) => setForm(prev => ({ ...prev, name: e.target.value }))}
              placeholder={locale === 'es-MX' ? 'Ej: Proyectos personales' : 'E.g.: Personal projects'}
              data-testid="idea-bucket-name-input"
              autoFocus
            />
          </div>

          <div className="space-y-2">
            <Label>{t('common.icon')}</Label>
            <Select 
              value={form.icon} 
              onValueChange={(value) => setForm(prev => ({ ...prev, icon: value }))}
            >
              <SelectTrigger data-testid="idea-bucket-icon-select">
                <SelectValue>
                  <div className="flex items-center gap-2">
                    <SelectedIcon className="w-4 h-4" />
                    {getIconLabel(IDEA_ICON_OPTIONS.find(i => i.id === form.icon), locale) || t('finance.other')}
                  </div>
                </SelectValue>
              </SelectTrigger>
              <SelectContent className="max-h-[300px]">
                <ScrollArea className="h-[280px]">
                  {IDEA_ICON_OPTIONS.map((icon) => {
                    const IconComponent = iconMap[icon.id] || Circle
                    return (
                      <SelectItem key={icon.id} value={icon.id}>
                        <div className="flex items-center gap-2">
                          <IconComponent className="w-4 h-4" />
                          {getIconLabel(icon, locale)}
                        </div>
                      </SelectItem>
                    )
                  })}
                </ScrollArea>
              </SelectContent>
            </Select>
          </div>

          {/* Preview */}
          <div className="pt-4 border-t">
            <Label className="text-xs text-muted-foreground">
              {t('common.preview')}
            </Label>
            <div className="mt-2 p-4 rounded-xl flex items-center gap-3 bg-amber-500/10">
              <SelectedIcon className="w-8 h-8 text-amber-600" />
              <span className="font-bold">
                {form.name || t('categoryModals.newIdeaBucket')}
              </span>
            </div>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={saving}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleSave} disabled={saving} data-testid="save-idea-bucket-btn">
            {saving 
              ? t('common.saving')
              : editingBucket 
                ? t('common.save')
                : t('common.create')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  )
}

/**
 * Quick add button component for inline bucket creation
 */
export function QuickAddIdeaBucketButton({ onBucketCreated, className = '' }) {
  const { t } = useLanguage()
  const [showModal, setShowModal] = useState(false)

  return (
    <>
      <button
        type="button"
        onClick={() => setShowModal(true)}
        className={`flex items-center gap-1 text-xs text-amber-600 hover:text-amber-500 transition-colors ${className}`}
        data-testid="quick-add-idea-bucket-btn"
      >
        <Plus className="w-3 h-3" />
        {t('categoryModals.newIdeaBucket')}
      </button>
      
      <IdeaBucketModal
        open={showModal}
        onOpenChange={setShowModal}
        onSuccess={onBucketCreated}
      />
    </>
  )
}
